import { Check, ChevronsUpDown, LoaderCircle, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
    Badge,
    Button,
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
    Label,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Text,
} from '@/ui/design-system/components';
import { formatVolume } from 'utils/helpers';
import { searchHashtags } from 'utils/api/hashtags.api';

export const MAX_PINNED_HASHTAGS = 20;

function WidgetHashtagMultiSelect({
    value,
    geo,
    onChange,
    id,
    label = 'Specific hashtags',
    maxItems = MAX_PINNED_HASHTAGS,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [hits, setHits] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const reqIdRef = useRef(0);

    useEffect(() => {
        const q = query.trim();
        if (!q) {
            reqIdRef.current += 1;
            setHits([]);
            setIsSearching(false);
            return undefined;
        }
        setIsSearching(true);
        const reqId = ++reqIdRef.current;
        const timer = setTimeout(async () => {
            try {
                const res = await searchHashtags({
                    query: q,
                    geo,
                    limit: 8,
                });
                if (reqId !== reqIdRef.current) return; // stale
                setHits(res?.data ?? []);
            } catch {
                if (reqId === reqIdRef.current) setHits([]);
            } finally {
                if (reqId === reqIdRef.current) setIsSearching(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [query, geo]);

    // Order forks the widget's data hash and the backend cache key.
    const emit = (names) => onChange([...new Set(names)].sort());

    const remove = (name) => emit(value.filter((v) => v !== name));

    const toggle = (name) => {
        if (value.includes(name)) {
            remove(name);
            return;
        }
        if (value.length >= maxItems) return;
        emit([...value, name]);
    };

    const atLimit = value.length >= maxItems;
    const fieldId = id ?? 'widget-pinned-hashtags';

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
                <Label
                    htmlFor={fieldId}
                    className="text-sm font-medium text-foreground"
                >
                    {label}
                </Label>
                <Text size="xs" color="muted">
                    Show these hashtags or leave empty for top results.
                </Text>
            </div>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id={fieldId}
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between font-normal"
                    >
                        <span
                            className={
                                value.length
                                    ? 'truncate'
                                    : 'text-muted-foreground'
                            }
                        >
                            {value.length
                                ? `${value.length} selected`
                                : 'Search for hashtags…'}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder="Search hashtags…"
                            value={query}
                            onValueChange={setQuery}
                        />
                        <CommandList>
                            <CommandEmpty>
                                {isSearching ? (
                                    <span className="flex items-center justify-center gap-2 text-muted-foreground">
                                        <LoaderCircle className="h-4 w-4 animate-spin" />
                                        Searching…
                                    </span>
                                ) : query.trim() ? (
                                    'No matches.'
                                ) : (
                                    'Type to search hashtags.'
                                )}
                            </CommandEmpty>
                            {hits.map((hit) => {
                                const isPinned = value.includes(
                                    hit.hashtag_name,
                                );
                                return (
                                    <CommandItem
                                        key={hit.hashtag_name}
                                        value={hit.hashtag_name}
                                        disabled={!isPinned && atLimit}
                                        onSelect={() =>
                                            toggle(hit.hashtag_name)
                                        }
                                    >
                                        <Check
                                            className={`mr-2 h-4 w-4 ${
                                                isPinned
                                                    ? 'opacity-100'
                                                    : 'opacity-0'
                                            }`}
                                        />
                                        <span className="flex flex-col">
                                            <span>{hit.hashtag_name}</span>
                                            {hit.avg_weekly_views != null && (
                                                <span className="text-[10px] uppercase text-muted-foreground">
                                                    {formatVolume(
                                                        hit.avg_weekly_views,
                                                    )}{' '}
                                                    weekly views
                                                </span>
                                            )}
                                        </span>
                                    </CommandItem>
                                );
                            })}
                            {atLimit && (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                    Up to {maxItems} hashtags can be selected.
                                </div>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {value.map((name) => (
                        <Badge
                            key={name}
                            variant="secondary"
                            interactive
                            className="h-6 px-1.5"
                            aria-label={`Remove ${name}`}
                            onClick={() => remove(name)}
                        >
                            <span className="text-xs">{name}</span>
                            <X className="h-3 w-3 text-muted-foreground" />
                        </Badge>
                    ))}
                </div>
            )}
        </div>
    );
}

export default WidgetHashtagMultiSelect;
